/**
 * Deterministic similar-listing scoring (Milestone 13).
 */
import { FAIR_PRICE_LABELS } from "../constants/automapFairPriceConstants";
import {
  isListingPubliclyVisible,
  shouldShowListingInSearch,
} from "./automapListing/listingVisibility";
import { resolveRiskLevel } from "./vehicleRiskViewModel";

function priceDeltaRatio(sourcePrice, candidatePrice) {
  if (sourcePrice == null || candidatePrice == null || sourcePrice <= 0) return null;
  return Math.abs(Number(candidatePrice) - Number(sourcePrice)) / Number(sourcePrice);
}

function yearDelta(sourceYear, candidateYear) {
  if (sourceYear == null || candidateYear == null) return null;
  return Math.abs(Number(sourceYear) - Number(candidateYear));
}

function isRecentlyConfirmed(listing) {
  if (!listing?.lastConfirmedAt) return false;
  const ts = Date.parse(listing.lastConfirmedAt);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= 14 * 24 * 60 * 60 * 1000;
}

/**
 * Score a candidate listing against a source listing.
 * Higher is better. Returns negative for excluded listings.
 */
export function scoreSimilarListing(sourceListing, candidateListing) {
  if (!sourceListing || !candidateListing) return -100;
  if (sourceListing.id && candidateListing.id && sourceListing.id === candidateListing.id) {
    return -100;
  }
  if (!shouldShowListingInSearch(candidateListing) || !isListingPubliclyVisible(candidateListing)) {
    return -50;
  }

  let score = 0;

  if (sourceListing.make && candidateListing.make === sourceListing.make) score += 30;
  if (sourceListing.model && candidateListing.model === sourceListing.model) score += 30;
  if (sourceListing.bodyType && candidateListing.bodyType === sourceListing.bodyType) score += 20;
  if (sourceListing.city && candidateListing.city === sourceListing.city) score += 15;
  if (sourceListing.dealerId && candidateListing.dealerId === sourceListing.dealerId) score += 10;

  const priceRatio = priceDeltaRatio(sourceListing.priceCop, candidateListing.priceCop);
  if (priceRatio != null) {
    if (priceRatio <= 0.15) score += 10;
    else if (priceRatio <= 0.25) score += 5;
  }

  const yearDiff = yearDelta(sourceListing.year, candidateListing.year);
  if (yearDiff != null) {
    if (yearDiff <= 2) score += 10;
    else if (yearDiff <= 5) score += 5;
  }

  const fairLabel = candidateListing.fairPriceLabel;
  if (fairLabel === FAIR_PRICE_LABELS.fair_price || fairLabel === FAIR_PRICE_LABELS.below_market) {
    score += 10;
  }

  if (candidateListing.dealerVerified) score += 5;
  if (isRecentlyConfirmed(candidateListing)) score += 5;

  const risk = resolveRiskLevel(candidateListing);
  if (risk === "high") score -= 20;

  if (candidateListing.priceCop == null) score -= 10;
  const imageCount = candidateListing.imageCount ?? candidateListing.images?.length ?? 0;
  if (imageCount === 0) score -= 10;

  return score;
}

export function sortScoredListings(scored = []) {
  return [...scored].sort((a, b) => b.score - a.score || String(a.listing.id).localeCompare(String(b.listing.id)));
}
